import React from 'react';
import { WatchtowerMark } from './WatchtowerMark';
export type EmailKind = 'expiry' | 'down' | 'digest';
interface EmailPreviewProps {
  kind: EmailKind;
  domain: string;
  days?: number;
  recipients: string[];
  reminderCadence?: number[];
  quietHours?: string | null;
}
export function EmailPreview({
  kind,
  domain,
  days = 9,
  recipients,
  reminderCadence,
  quietHours
}: EmailPreviewProps) {
  const subject = kind === 'expiry' ? `Certificate for ${domain} expires in ${days} days` : kind === 'down' ? `${domain} is not responding` : `Daily digest — ${domain} and 5 other sites`;
  return <div className="overflow-hidden rounded-card border border-line bg-surface shadow-raised" data-id="element-272" data-mp="glknmifxqlsw">
      <div className="border-b border-line bg-canvas px-5 py-3" data-id="element-273" data-mp="bnj6i322kwk2">
        <div className="flex items-center gap-2" data-id="element-274" data-mp="31dwb6yf9mt5e">
          <WatchtowerMark className="h-6 w-6 shrink-0 text-brand" data-id="element-275" data-mp="31eo39z9nm8qx" />
          <span className="text-[13px] font-medium text-ink" data-id="element-276" data-mp="2wd8nalmc2sfe">
            Watchtower alerts
          </span>
          <span className="font-mono text-xs text-ink-soft" data-id="element-277" data-mp="2fcm59bge032n">
            &lt;alerts@watchtowerworks.com&gt;
          </span>
        </div>
        <p className="mt-2 text-sm font-semibold text-ink" data-id="element-278" data-mp="2jajxkn5gf5a9">{subject}</p>
        <p className="mt-0.5 truncate text-xs text-ink-soft" data-id="element-279" data-mp="25cvcpfbdzy57">
          To {recipients.length > 0 ? recipients.join(', ') : 'no recipients yet'}
        </p>
      </div>

      <div className="px-5 py-5" data-id="element-280" data-mp="1vm93c2u9k3uy">
        {kind === 'expiry' && <>
            <p className="text-sm leading-relaxed text-ink" data-id="element-281" data-mp="20ek417wtvtok">
              The TLS certificate for <strong data-id="element-282" data-mp="1mmvr52er916j">{domain}</strong> expires in{' '}
              <strong data-id="element-283" data-mp="1mo6s6ugtfuty">{days} days</strong>. Renew it before then and visitors
              will never see a warning.
            </p>
            <div className="mt-4 rounded-control border border-warn-border bg-warn-soft px-4 py-3" data-id="element-284" data-mp="1cog00zfq9rx1">
              <p className="tabular text-[13px] font-medium text-warn-ink" data-id="element-285" data-mp="1hnj52jvpjy50">
                {days} days remaining
              </p>
              <p className="mt-1 text-xs text-warn-ink/80" data-id="element-286" data-mp="yj5up895qny8">
                Issued by DigiCert TLS RSA SHA256 2020 CA1
              </p>
            </div>
            {reminderCadence && reminderCadence.length > 0 && <p className="mt-4 text-xs leading-relaxed text-ink-soft" data-id="element-287" data-mp="otw642divnev">
                You will be reminded again at{' '}
                {reminderCadence.join(', ')} days before expiry.
              </p>}
          </>}

        {kind === 'down' && <>
            <p className="text-sm leading-relaxed text-ink" data-id="element-288" data-mp="1cjqhsgm3v5xj">
              <strong data-id="element-289" data-mp="17jc6mpp7jfva">{domain}</strong> failed 3 checks in a row and is being
              treated as down. The last response was HTTP 500 from{' '}
              <span className="tabular" data-id="element-290" data-mp="12jzky8a2oeua">/v1/health</span>.
            </p>
            <div className="mt-4 rounded-control border border-critical-border bg-critical-soft px-4 py-3" data-id="element-291" data-mp="3sa37214jz6jy">
              <p className="text-[13px] font-medium text-critical-ink" data-id="element-292" data-mp="3n9ox2dje5mja">
                Down since 09:14 · checked every minute
              </p>
              <p className="mt-1 text-xs text-critical-ink/80" data-id="element-293" data-mp="22quwr6g2lc4m">
                We will email again the moment it recovers.
              </p>
            </div>
          </>}

        {kind === 'digest' && <>
            <p className="text-sm leading-relaxed text-ink" data-id="element-294" data-mp="2hi3uy3y1g5td">
              One summary a day instead of individual alerts. Everything that
              changed in the last 24 hours, in one place.
            </p>
            <ul className="mt-4 space-y-2 text-[13px] text-ink-muted" data-id="element-295" data-mp="2qhiiovmtm2a6">
              <li className="flex justify-between gap-4 border-b border-line pb-2" data-id="element-296" data-mp="2vhd7mtn1chsl">
                <span data-id="element-297" data-mp="2vfk2xjvzg7n8">{domain}</span>
                <span className="tabular text-warn-ink" data-id="element-298" data-mp="30gpvnhpn7uwr">
                  {days} days to expiry
                </span>
              </li>
              <li className="flex justify-between gap-4 border-b border-line pb-2" data-id="element-299" data-mp="1156nh5c5yqo5">
                <span data-id="element-300" data-mp="163883up8my1q">api.acme.com</span>
                <span className="text-critical-ink" data-id="element-301" data-mp="2wjk5n467dn2n">Down 52 min</span>
              </li>
              <li className="flex justify-between gap-4" data-id="element-302" data-mp="2wln2a71v2egc">
                <span data-id="element-303" data-mp="2rjectko75uiz">4 other sites</span>
                <span className="text-healthy-ink" data-id="element-304" data-mp="2rmtpyhyb8thr">No change</span>
              </li>
            </ul>
          </>}

        {quietHours && <p className="mt-4 border-t border-line pt-3 text-xs text-ink-soft" data-id="element-305" data-mp="31jonoymrepuz">
            Quiet hours {quietHours} — non-critical alerts are held until
            morning.
          </p>}
      </div>
    </div>;
}